import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, LogIn, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Prompts a guest user to sign in when they try to save/favorite/bookmark
 * anything across the app. Shows a soft nudge with two CTAs and preserves
 * the current URL so we can bring them right back after auth.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - action?: string — short label describing what they were saving
 *                       (e.g. "save this reflection", "favorite this fact").
 */
export default function SignInRequiredModal({ open, onClose, action }) {
  const navigate = useNavigate();
  if (!open) return null;

  const returnTo = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
  const goSignup = () => {
    onClose?.();
    navigate(`/signup?returnTo=${encodeURIComponent(returnTo)}`);
  };
  const goLogin = () => {
    onClose?.();
    navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
      data-testid="sign-in-required-backdrop"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 shadow-2xl relative"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="sign-in-required-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full transition-all hover:bg-[var(--theme-hover)]"
          style={{ color: 'var(--theme-textSecondary)' }}
          aria-label="Close"
          data-testid="sign-in-required-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ background: 'var(--theme-accentLight)', color: 'var(--theme-accent)' }}
        >
          <Bookmark className="h-6 w-6" />
        </div>

        <h3
          className="text-lg font-semibold mb-2"
          style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-text)' }}
          data-testid="sign-in-required-title"
        >
          Sign in to save
        </h3>
        <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--theme-textSecondary)' }}>
          Create a free account to {action || 'save this'} and keep your notes, highlights and
          favorites in one place — synced across devices.
        </p>

        <div className="flex flex-col gap-2">
          <Button
            onClick={goSignup}
            className="w-full rounded-xl py-3 text-sm font-medium"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid="sign-in-required-signup-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Create free account
          </Button>
          <Button
            onClick={goLogin}
            variant="outline"
            className="w-full rounded-xl py-3 text-sm font-medium"
            style={{ borderColor: 'var(--theme-border)', color: 'var(--theme-text)' }}
            data-testid="sign-in-required-login-btn"
          >
            <LogIn className="h-4 w-4 mr-2" />
            I already have an account
          </Button>
        </div>
      </div>
    </div>
  );
}
